import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { questionAsked, resetQuestionInBox } from "../../state/slice";
import { motion, AnimatePresence } from "framer-motion";
import "./enhanced-question.scss";
import { useSelector } from "react-redux";

/**
 * Enhanced Question Component
 *
 * Provides a textarea input for user questions with animations and responsive design
 *
 * @param {object} props - Component props
 * @param {string} props.type - Either "initial" (main question area) or "bottom" (follow-up question area)
 */
const EnhancedQuestion = (props: { type: "initial" | "bottom" }) => {
  // State and refs
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useDispatch();
  const question = useSelector((state: any) => state.tempQuestion);

  /**
   * Handle form input and auto-resize textarea
   */
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);

    // Auto-resize textarea based on content
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        300
      )}px`;
    }
  };

  /**
   * Clear the input field and focus it
   */
  const handleClear = useCallback(() => {
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
    }
  }, []);

  /**
   * Submit the question, clear the input, and reset the question box
   */
  const submitQuestion = useCallback(() => {
    if (!value.trim()) return;
    dispatch(questionAsked("")); // clear it first
    setTimeout(() => {
      dispatch(questionAsked(value)); // set again even if same string
    }, 0); // defer to next tick

    setValue("");
    dispatch(resetQuestionInBox());

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, dispatch]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter to submit
      if (e.ctrlKey && e.key === "Enter") {
        submitQuestion();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [submitQuestion]); // Fixed dependency array

  // Set value when question from Redux store changes (for suggested questions)
  useEffect(() => {
    if (question && question !== "") {
      setValue(question);

      // Focus the textarea and adjust height
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.style.height = "auto";
          textareaRef.current.style.height = `${Math.min(
            textareaRef.current.scrollHeight,
            300
          )}px`;
        }
      }, 10);
    }
  }, [question]);

  const renderInitialForm = () => {
    return (
      <div className="enhanced-question-container">
        <div className="question-card">
          <textarea
            ref={textareaRef}
            id="question"
            className="question-textarea"
            value={value}
            onChange={handleInput}
            placeholder="Ask Tim any contracting or commercial sector related questions.."
            rows={4}
            aria-label="Question input field"
          />

          <AnimatePresence>
            {value.trim() && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="clear-button"
                onClick={handleClear}
                aria-label="Clear input"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </motion.button>
            )}
          </AnimatePresence>

          <button
            className={`submit-button ${!value.trim() ? "disabled" : ""}`}
            type="button"
            onClick={submitQuestion}
            disabled={!value.trim()}
            aria-label="Submit question"
            title="Submit"
          >
            <span>Submit</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderBottomForm = () => {
    return (
      <div className="enhanced-question-container bottom-container">
        <div className="question-card">
          <textarea
            ref={textareaRef}
            id="follow-up-question"
            className="question-textarea"
            value={value}
            onChange={handleInput}
            placeholder="Have another question? Ask away..."
            rows={2}
            aria-label="Follow-up question input field"
          />

          <AnimatePresence>
            {value.trim() && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="clear-button"
                onClick={handleClear}
                aria-label="Clear input"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </motion.button>
            )}
          </AnimatePresence>

          <button
            className={`submit-button ${!value.trim() ? "disabled" : ""}`}
            type="button"
            onClick={submitQuestion}
            disabled={!value.trim()}
            aria-label="Submit follow-up question"
            title="Submit"
          >
            <span>Submit</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return props.type === "bottom" ? renderBottomForm() : renderInitialForm();
};

export default EnhancedQuestion;
